import {type BootstrapThemeColour} from '@/components/shared/bootstrap';

export type SphereFeatureCardValue = {
  image: number | null;
  src?: string;
  image_alt: string;
  title: string;
  description: string;
  status: '' | 'planned' | 'in_progress' | 'released';
  target: string;
  link_label: string;
  page: number | null;
  internal_page_url?: string;
  external_url: string;
};

export type SphereLinkCardValue = {
  title: string;
  icon: string; // Google Material Symbols name, e.g. 'folder_open'
  links: {label: string; page: number | null; internal_page_url?: string; external_url: string}[];
};

type SphereBlockValues = {
  block_quote: {attribute_name: string; text: string};
  component_block: {kwargs: any; name: string};
  contextual_block: {text: string; type: BootstrapThemeColour};
  embed_block: string;
  feature_highlight_block: SphereFeatureCardValue & {align: 'left' | 'right'};
  carousel_block: {heading: string; options: SphereFeatureCardValue[]};
  link_cards_block: {heading: string; cards: SphereLinkCardValue[]};
  heading_block: {heading_text: string; size: 'h1' | 'h2' | 'h3'};
  image_block: {alt: string; caption: string; image: number; src: string};
  paragraph_block: string;
  table_block: {
    caption: string;
    columns: {type: string; heading: string}[];
    rows: {values: (string | number)[]}[];
  };
  resource_group: SphereResourceGroupBlockValue;
  group_block: {title: string; content: SphereBlock[]};
  report_block: SphereReport;
};

// Technically a block, but is available through the `ResourceGroupBlock` class.
type SphereLinkBlockValue = {
  title: string;
  description: string;
  page?: number;
  external_url?: string;
  internal_page_url?: string;
};

type SphereResourceGroupBlockValue = {
  group_icon: string;
  group_title: string;
  links: SphereLinkBlockValue[];
};

export type SphereBlock<T extends keyof SphereBlockValues = keyof SphereBlockValues> = {
  id: string;
  type: T;
  value: SphereBlockValues[T];
};

// The actual API response structure from /api/cms/dashboard/
export type SphereWidget = {
  meta: SpherePageMeta<'cms.Widget'>;
  widget_type?: string;
  full_url: string | null;
  refresh_interval: number;
  page: SpherePage<SpherePageType> | null;
  page_redirect_query_params: Record<string, string>;
  description: string;
  x_scale_title: string;
  y_scale_title: string;
  max_w: number | null;
  min_w: number;
  max_h: number | null;
  min_h: number;
};

export type SphereFolder = {
  name: string;
  allowUploads: boolean;
  container: string;
  containerPath: string;
  folderPath: string;
  fullPath: string;
  storageAccount: string;
  message?: string;
  token: {accessToken: string; tokenExpiry: string};
};

export type SphereReport = {
  id: number;
  context_message: string;
  description: string;
  report_id: string;
  title: string;
  token: {accessToken: string; embedUrl: string; tokenExpiry: string; tokenId: string};
};

export type SphereIFrame = {
  auth_scopes: string;
  class_name: string;
  url: string;
};

// Ekco users only: the last 90 days
export type PageEngagement = {
  unique_users: number;
  unique_ekco_users: number;
  unique_customer_users: number;
  ekco_views_90d: number;
  customer_views_90d: number;
};

export type SpherePageTypeCore =
  | 'cms.SiteRootPage'
  | 'cms.DashboardPage'
  | 'cms.CoreIndexPage'
  | 'cms.NewsIndexPage';
export type SpherePageTypePage =
  | 'cms.CategoryIndexPage'
  | 'cms.ReportPage'
  | 'cms.DocumentPage'
  | 'cms.FolderPage'
  | 'cms.DynamicPage'
  | 'cms.IFramePage'
  | 'cms.ExternalMenuLink'
  | 'cms.StoragePage'
  | 'cms.GroupPage';
export type SpherePageTypeWidget = 'cms.Widget';
export type SpherePageType = SpherePageTypeCore | SpherePageTypePage | SpherePageTypeWidget;

export type SpherePageMeta<T extends SpherePageType> = {
  id: number;
  title: string;
  type: T extends SpherePageTypeCore ? SpherePageTypeCore : SpherePageTypePage;
  detail_url: string;
  slug: string;
  locale: 'en' | 'nl';
  depth: number;
  url_path: string;
  path: string;
  icon?: string | null;
  show_in_menus: boolean;
  has_route?: boolean;
  external_url?: string; // ExternalMenuLink: opened in a new tab instead of navigating
  is_excluded?: boolean; // page has excluded_products yet is listed: shown greyed in the menu
  has_content?: boolean; // CoreIndexPage & CategoryIndexPage: only those with content get a page
  translation_key?: string;
  // these are only returned to ekco users:
  view_permissions?: {key: string; roles: {name: string; type: string}[]}[];
  products?: {sku: string; name: string}[];
  excluded_products?: {sku: string; name: string}[];
  public?: boolean;
  engagement?: PageEngagement;
};

import {LimitOffsetPagination} from '@/api/paginatedResults'; // api/cms/pages/
export type PaginatedSpherePage = LimitOffsetPagination<SpherePage<SpherePageType>[]>;

// api/cms/pages/n
export type SpherePage<T extends SpherePageType> = {
  id: number;
  meta: SpherePageMeta<T>;
  content: SphereBlock[];
  document: any | null;
  introduction?: string; // CoreIndexPage & CategoryIndexPage
  report?: SphereReport;
  folder?: SphereFolder;
  iframe?: SphereIFrame;
  group?: SphereBlock<'group_block'>[];
  additional_resources?: SphereBlock<'resource_group'>[];
};
// api/cms/dashboard/
export type DashboardNotice = {
  // Values are ContextualBlock's type choices — Bootstrap variants
  type: 'info' | 'danger' | 'primary' | 'secondary' | 'success' | 'warning';
  body: string;
};

export type PaginatedSphereWidget = LimitOffsetPagination<SphereWidget[]> & {
  notice: DashboardNotice | null;
};

export type DashboardPage = SpherePage<'cms.DashboardPage'>;
export type ReportPage = SpherePage<'cms.ReportPage'>;
export type DocumentPage = SpherePage<'cms.DocumentPage'>;
export type FolderPage = SpherePage<'cms.FolderPage'>;
export type DynamicPage = SpherePage<'cms.DynamicPage'>;
export type IFramePage = SpherePage<'cms.IFramePage'>;

export type TreeNodeCore = {
  id: number;
  title: string;
  meta: SpherePageMeta<SpherePageTypeCore>;
  expanded: boolean;
  disabled: boolean;
  children: TreeNodePage[];
  node_type: 'CoreNode';
};
export type TreeNodePage = {
  id: number;
  title: string;
  meta: SpherePageMeta<SpherePageTypePage>;
  expanded: boolean;
  disabled: boolean;
  children: TreeNodePage[];
  parent?: TreeNodeCore | TreeNodePage;
  node_type: 'PageNode';
};
export type TreeNav = TreeNodeCore[];

function makeCoreNode(meta: SpherePageMeta<SpherePageTypeCore>): TreeNodeCore {
  return {
    id: meta.id,
    title: meta.title,
    meta,
    expanded: false,
    children: [],
    node_type: 'CoreNode',
    disabled: false,
  };
}

function makePageNode(
  meta: SpherePageMeta<SpherePageTypePage>,
  parent?: TreeNodeCore | TreeNodePage,
): TreeNodePage {
  return {
    id: meta.id,
    title: meta.title,
    meta,
    expanded: false,
    children: [],
    parent,
    node_type: 'PageNode',
    // A category without content of its own is only a folder, so hide it until it has children.
    disabled: meta.type === 'cms.CategoryIndexPage' && !meta.has_content,
  };
}

/**
 * Page types that are never part of the navigation tree (widgets, site root).
 * Extend this list to keep further types out of the menu.
 */
export const NAVIGATION_EXCLUDED_TYPES: SpherePageType[] = ['cms.Widget', 'cms.SiteRootPage'];

/**
 * `/api/cms/pages` returns an ordered, flattened tree of all pages.
 * Pages are ordered in depth-first traversal, so children always follow their parent.
 *
 * New page structure:
 * Root (wagtailcore.Page)
 * └── SiteRootPage (depth=2, ignored - transparent in navigation)
 *     ├── DashboardPage (depth=3, core node)
 *     │   └── Widget (depth=4, ignored)
 *     ├── CoreIndexPage (depth=3, core node)
 *     │   ├── CategoryIndexPage (depth=4, page node)
 *     │   ├── ReportPage (depth=4, page node)
 *     │   └── ...
 *     ├── NewsIndexPage (depth=3, core node)
 *     │   └── NewsPage (depth=4, page node)
 *     ├── IFramePage (depth=3, core node - renders as direct link)
 *     └── ...
 */
export type Navigation = {
  /** Nodes shown in the menu. */
  tree: TreeNav;
  /**
   * Pages with `show_in_menus === false` (and their descendants). They are not part of `tree`,
   * but still need a route so that links to them resolve. `parent` points at the closest node
   * so the menu can still highlight where they live.
   */
  hiddenNodes: (TreeNodeCore | TreeNodePage)[];
};

export const makeNavigation = (pages: SpherePage<SpherePageType>[]): Navigation => {
  const coreNodes: TreeNodeCore[] = [];
  const hiddenNodes: (TreeNodeCore | TreeNodePage)[] = [];
  const pagesMap = new Map<string, TreeNodeCore | TreeNodePage>();
  const hiddenPaths: string[] = [];

  /**
   * Path structure with new hierarchy:
   * 0001 - Root (depth=1, ignored)
   * 00010001 - SiteRootPage (depth=2, ignored)
   * 000100010001 - DashboardPage/CoreIndexPage/NewsIndexPage/IFramePage (depth=3)
   * 0001000100010001 - CategoryIndexPage/ReportPage/NewsPage/Widget (depth=4)
   */

  for (const page of pages) {
    const path = page.meta.path;
    const parentPath = path.slice(0, -4);
    const parent = pagesMap.get(parentPath);
    const depth = path.length / 4;

    // Skip ignored types
    if (NAVIGATION_EXCLUDED_TYPES.includes(page.meta.type)) continue;

    // Skip if path is too short (root or site root level)
    if (depth < 3) continue;

    // Pages hidden from menus (and their descendants) get a route, but no place in the menu.
    const hidden = page.meta.show_in_menus === false || hiddenPaths.some((p) => path.startsWith(p));
    if (hidden) hiddenPaths.push(path);

    // Pages at depth 3 are always "core nodes" (top-level sidebar items)
    // The route handler determines if they're expandable (CoreIndexPage) or direct links (IFramePage)
    // A CoreIndexPage with content is both: it renders its own page and expands its children.
    if (depth === 3) {
      const node = makeCoreNode(page.meta);
      // CoreIndexPage without content or children should be disabled (will be enabled when children are added)
      // Other types (DashboardPage, IFramePage, NewsIndexPage) are directly navigable
      node.disabled = page.meta.type === 'cms.CoreIndexPage' && !page.meta.has_content;
      pagesMap.set(path, node);
      (hidden ? hiddenNodes : coreNodes).push(node);
    } else {
      // Depth 4+ are page nodes (children of cores)
      const node = makePageNode(page.meta, parent);
      pagesMap.set(path, node);

      if (hidden) {
        hiddenNodes.push(node);
      } else if (parent) {
        parent.children.push(node);
        // Enable parent if it has at least one navigable child
        parent.disabled = false;
      } else {
        console.error('Page node without parent:', page);
      }
    }
  }

  return {tree: coreNodes, hiddenNodes};
};

export const makeCoreNavigationTree = (pages: SpherePage<SpherePageType>[]): TreeNav =>
  makeNavigation(pages).tree;
